import { START_LOCATION, type NavigationGuardWithThis } from 'vue-router'
import { ApiError, getActiveOrgId } from '@/api/client'
import { getLegacyBridge, type LegacyBridge } from '@/api/legacy/bridge'

/** What the hand-over route said about one organization: a token, or why there is none. */
export type BridgeOutcome = { answer: LegacyBridge } | { failure: unknown }

/** What entering the organization does about it. */
export type BridgeDecision =
  /** The previous platform serves it: the browser goes there, and this page is left. */
  | { action: 'hand-over'; location: string }
  /** It opens here, and the answer holds for as long as this page lives. */
  | { action: 'stay'; settled: true }
  /** It opens here because the question failed; the next entry asks again. */
  | { action: 'stay'; settled: false; failure: unknown }

/**
 * From the route's answer to what the navigation does. Pure, so that each answer can be checked
 * without a browser or a previous platform.
 */
export function decideBridge(outcome: BridgeOutcome): BridgeDecision {
  if ('answer' in outcome) {
    const { bridge_url, token } = outcome.answer
    return { action: 'hand-over', location: `${bridge_url}?token=${encodeURIComponent(token)}` }
  }
  const { failure } = outcome
  if (failure instanceof ApiError) {
    // This backend serves the organization, it never lived over there, or the caller's staff seat
    // opens it here: nothing to hand over to.
    if (failure.status === 400 && failure.code === 'validation') return { action: 'stay', settled: true }
    // The route is not mounted: this deployment has no previous platform, as in development. The
    // other 404, "not a member", does not reach here — the session guard has checked the membership.
    if (failure.status === 404) return { action: 'stay', settled: true }
  }
  return { action: 'stay', settled: false, failure }
}

/**
 * The hand-over of an organization the previous platform still serves: entering one sends the
 * browser there, instead of opening an organization this backend holds nothing of yet.
 *
 * **It asks on the way into every organization**, because nothing on this side says which ones are
 * still over there: `/access` carries no such flag, and the route refuses an organization this
 * backend serves, so the question is its own answer. An answer holds for the organization until the
 * page is reloaded. A failure opens the organization here rather than locking the member out of
 * it, is logged, and is asked again on the next entry.
 *
 * **A guard of its own, registered straight after the session guard**, for two reasons. The order
 * is what makes a `404` unambiguous: global guards run one after another and stop at the first that
 * does not let the navigation through, so by the time this one runs `X-Org-Id` names the
 * organization and the caller is a live member of it, and a `404` can only be a route that is not
 * mounted. And the bridge dies with the migration, which the backend confines to one crate mounted
 * behind one `if`: here it is this file, `src/api/legacy/` and one line of `main.ts`, where folded
 * into the session guard it would be a branch in a file that stays.
 *
 * **A staff seat that operates the platform is never handed over**, and the route is what says so:
 * it answers its holder `400 validation`, as it answers an organization this backend serves.
 *
 * **The browser leaves with a top-level GET**, never a form or a frame: the verifier over there
 * answers a GET and nothing else. On the page's first navigation (a deep link, a reload) the entry
 * being left is the organization's own URL, which would hand over again on Back, so it is replaced
 * rather than kept.
 */
export function createLegacyBridgeGuard(): NavigationGuardWithThis<undefined> {
  // Organizations whose answer holds for the rest of this page's life.
  const settled = new Set<string>()

  return async (to, from) => {
    const orgId = to.params.organizationId
    if (typeof orgId !== 'string' || orgId === '') return true
    // Asked on the way into an organization, never on a move inside one.
    if (from.params.organizationId === orgId || settled.has(orgId)) return true
    // The session guard lets an organization through only once it has set the header for it. A
    // boot that failed lets every navigation through too, and must not reach the question.
    if (getActiveOrgId() !== orgId) return true

    const decision = decideBridge(
      await getLegacyBridge(orgId).then(
        (answer) => ({ answer }),
        (failure: unknown) => ({ failure }),
      ),
    )
    if (decision.action === 'hand-over') {
      if (from === START_LOCATION) window.location.replace(decision.location)
      else window.location.assign(decision.location)
      return false
    }
    if (decision.settled) settled.add(orgId)
    else console.error(`The hand-over check failed for organization ${orgId}; opening it here.`, decision.failure)
    return true
  }
}
